import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  Brain,
  FileText,
  ClipboardList,
  Shield,
  MessageCircle,
  Sparkles,
  Users,
  ArrowRight,
  Star,
  Presentation,
  Gamepad2,
  BarChart3,
  FileSpreadsheet,
  Zap,
  CheckCircle,
  Globe,
  Clock,
  Play,
  BookOpen,
  Award,
  Target,
  TrendingUp,
  Lightbulb,
  Rocket,
  Camera,
  FileImage,
  Link,
  Upload,
  Wand2,
  Bot,
  ChevronRight,
  Monitor,
  Smartphone,
  Tablet,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  School,
  GraduationCapIcon,
  Building,
  Home,
  PlusCircle,
  Timer,
  Palette,
  BarChart,
  Headphones } from
"lucide-react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const HomePage = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const useCases = [
  {
    icon: School,
    title: "High School Mathematics Teacher",
    description: "Sarah creates engaging algebra lessons in minutes, generates interactive quizzes, and tracks student progress with AI-powered analytics.",
    benefits: ["Saves 5 hours per week", "Increased student engagement by 40%", "Improved test scores by 25%"],
    cta: "Create Math Lessons",
    color: "from-blue-500 to-indigo-600"
  },
  {
    icon: Building,
    title: "Corporate Training Manager",
    description: "Convert dry compliance training into gamified learning experiences that employees actually enjoy and remember.",
    benefits: ["90% completion rates", "Reduced training costs by 60%", "Better retention scores"],
    cta: "Gamify Training",
    color: "from-purple-500 to-pink-600"
  },
  {
    icon: GraduationCapIcon,
    title: "University Professor",
    description: "Generate comprehensive lecture slides, create plagiarism-free assignments, and provide personalized feedback at scale.",
    benefits: ["Handle 200+ students easily", "Detect AI-generated content", "Automated grading"],
    cta: "Scale Your Teaching",
    color: "from-green-500 to-teal-600"
  },
  {
    icon: Home,
    title: "Homeschooling Parent",
    description: "Access curriculum-aligned lesson plans, create engaging activities, and track your child's learning progress.",
    benefits: ["Save $2000+ on materials", "Personalized learning paths", "Progress tracking"],
    cta: "Start Homeschooling",
    color: "from-orange-500 to-red-600"
  }];


  const faqItems = [
  {
    question: "How does the AI generate lesson plans?",
    answer: "Our AI uses advanced natural language processing to create curriculum-aligned lesson plans based on your subject, grade level, and learning objectives. Each plan includes activities, assessments, and differentiation strategies tailored to your specific needs."
  },
  {
    question: "Can I customize the generated content?",
    answer: "Absolutely! All AI-generated content is fully editable. You can modify lesson plans, adjust quiz questions, change presentation templates, and customize everything to match your teaching style and student needs."
  },
  {
    question: "How accurate is the plagiarism detection?",
    answer: "Our plagiarism detection system uses advanced AI algorithms to identify both traditional plagiarism and AI-generated content with over 95% accuracy. It checks against billions of web pages, academic papers, and known AI writing patterns."
  },
  {
    question: "Is my data secure and private?",
    answer: "Yes, we take data security very seriously. All data is encrypted in transit and at rest, we comply with GDPR and COPPA regulations, and we never share your content with third parties. Your lesson plans and student data remain completely private."
  },
  {
    question: "What devices and platforms are supported?",
    answer: "Soyous AI works on all modern devices - desktop computers, laptops, tablets, and smartphones. We have a web app that works in any browser and a native Android app. iOS app is coming soon!"
  },
  {
    question: "How much does it cost after the free trial?",
    answer: "We offer flexible pricing plans starting at $19/month for individual teachers, with discounts for schools and districts. All plans include unlimited AI generations, full feature access, and priority support. No hidden fees or per-student charges."
  },
  {
    question: "Can I use this for online and hybrid teaching?",
    answer: "Yes! Our platform is perfect for online, hybrid, and in-person teaching. Create interactive presentations, run live quiz games, share digital lesson plans, and engage students whether they're in the classroom or learning from home."
  },
  {
    question: "What subjects and grade levels are supported?",
    answer: "We support all major subjects (Math, Science, English, History, etc.) and grade levels from K-12 through university. Our AI is trained on curriculum standards from multiple countries and can adapt to your specific educational requirements."
  }];


  const mainFeatures = [
  {
    icon: Brain,
    title: "AI Lesson Plans",
    description: "Generate comprehensive, curriculum-aligned lesson plans in seconds with advanced AI",
    color: "from-blue-400 to-blue-600",
    features: ["Curriculum alignment", "Multiple grade levels", "Subject-specific content", "Customizable templates"]
  },
  {
    icon: Presentation,
    title: "AI Presentation Generator",
    description: "Create stunning, interactive presentations with AI-powered content and design",
    color: "from-purple-400 to-purple-600",
    features: ["Auto-generated slides", "Professional templates", "Image integration", "Export to PDF"]
  },
  {
    icon: ClipboardList,
    title: "Smart Quiz Builder",
    description: "Build engaging quizzes with multiple question types and automatic grading",
    color: "from-green-400 to-green-600",
    features: ["Multiple question types", "Auto-grading", "Analytics", "Resource-based generation"]
  },
  {
    icon: Gamepad2,
    title: "Gamification Engine",
    description: "Transform learning into interactive games with real-time multiplayer quizzes",
    color: "from-pink-400 to-pink-600",
    features: ["Real-time multiplayer", "Leaderboards", "QR code joining", "Live analytics"]
  },
  {
    icon: Shield,
    title: "AI Plagiarism Detection",
    description: "Advanced plagiarism and AI content detection for academic integrity",
    color: "from-orange-400 to-orange-600",
    features: ["AI content detection", "Plagiarism checking", "Detailed reports", "Multiple file formats"]
  },
  {
    icon: FileSpreadsheet,
    title: "Grade Management",
    description: "Excel-compatible grade management with advanced calculations and analytics",
    color: "from-teal-400 to-teal-600",
    features: ["Excel integration", "Auto-calculations", "Grade analytics", "Export options"]
  }];


  const aiCapabilities = [
  {
    icon: Bot,
    title: "24/7 AI Teaching Assistant",
    description: "Get instant help with teaching strategies, curriculum questions, and educational guidance"
  },
  {
    icon: FileImage,
    title: "Multi-Format Content Processing",
    description: "Extract questions from PDFs, images, screenshots, and web links automatically"
  },
  {
    icon: Wand2,
    title: "Intelligent Content Generation",
    description: "Create lesson plans, quizzes, and presentations tailored to your specific needs"
  },
  {
    icon: BarChart3,
    title: "Advanced Analytics",
    description: "Track student progress, quiz performance, and engagement metrics in real-time"
  }];


  const stats = [
  { label: "Active Educators", value: "2,500+", icon: Users },
  { label: "AI Presentations Created", value: "15,000+", icon: Presentation },
  { label: "Interactive Games Played", value: "50,000+", icon: Gamepad2 },
  { label: "Questions Generated", value: "100,000+", icon: ClipboardList }];


  const testimonials = [
  {
    name: "Sarah Chen",
    role: "High School Mathematics Teacher",
    content: "The AI presentation generator has completely transformed how I create engaging lessons. What used to take hours now takes minutes!",
    rating: 5
  },
  {
    name: "Dr. Michael Rodriguez",
    role: "University Professor",
    content: "The gamification features have increased student engagement by 300%. My students actually look forward to quiz time now!",
    rating: 5
  },
  {
    name: "Emily Johnson",
    role: "Elementary School Teacher",
    content: "The AI assistant helps me create differentiated content for all my students. It's like having a teaching expert available 24/7.",
    rating: 5
  }];


  const pricingFeatures = [
  "Unlimited AI-generated lesson plans",
  "Advanced presentation creation",
  "Interactive quiz games",
  "Plagiarism detection",
  "Grade management system",
  "24/7 AI teaching assistant",
  "Multi-format content processing",
  "Advanced analytics dashboard"];


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-id="el3te3u4c" data-path="src/pages/HomePage.tsx">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-3 sticky top-0 z-50" data-id="9r4mydu6x" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="v6gdwu8b3" data-path="src/pages/HomePage.tsx">
          <div className="flex items-center space-x-3" data-id="02n8dw2ns" data-path="src/pages/HomePage.tsx">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg" data-id="3h174a99a" data-path="src/pages/HomePage.tsx">
              <GraduationCap className="h-6 w-6 text-white" data-id="2sjl6e1n4" data-path="src/pages/HomePage.tsx" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="ohkl5biza" data-path="src/pages/HomePage.tsx">
              Soyous AI
            </span>
          </div>
          <div className="flex items-center space-x-3" data-id="ff2fiqw6y" data-path="src/pages/HomePage.tsx">
            <Button
              variant="outline"
              onClick={() => navigate('/register')}
              className="border-gray-300 hover:bg-gray-50" data-id="71ywf8g4m" data-path="src/pages/HomePage.tsx">
              Register
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="tzffqn6rc" data-path="src/pages/HomePage.tsx">
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-4 py-20 relative overflow-hidden" data-id="2ekpqplbf" data-path="src/pages/HomePage.tsx">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl mx-4" data-id="wwepx47bv" data-path="src/pages/HomePage.tsx"></div>
        <div className="max-w-7xl mx-auto text-center relative" data-id="chzdh6qfh" data-path="src/pages/HomePage.tsx">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} data-id="fm7y6r7e1" data-path="src/pages/HomePage.tsx">
            
            <Badge className="mb-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 text-sm" data-id="yuhnoa3yz" data-path="src/pages/HomePage.tsx">
              <Sparkles className="h-4 w-4 mr-2" data-id="uwfbpt7c0" data-path="src/pages/HomePage.tsx" />
              Powered by Advanced AI • Trusted by 2,500+ Educators
            </Badge>
            
            <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6" data-id="25w1hbznp" data-path="src/pages/HomePage.tsx">
              The Complete
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent block" data-id="25rb8eog4" data-path="src/pages/HomePage.tsx">
                AI Teaching Platform
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed" data-id="2pu812vg0" data-path="src/pages/HomePage.tsx">
              From AI-powered lesson plans and interactive presentations to gamified quizzes and plagiarism detection - 
              everything you need to revolutionize your teaching in one comprehensive platform.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8" data-id="a14kjh0ev" data-path="src/pages/HomePage.tsx">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-8 py-4 text-lg" data-id="tk3nby9ld" data-path="src/pages/HomePage.tsx">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" data-id="5067pffk2" data-path="src/pages/HomePage.tsx" />
              </Button>
            </div>
            
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-500" data-id="57xo2r3ul" data-path="src/pages/HomePage.tsx">
              <div className="flex items-center" data-id="lsqfzpgwf" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" data-id="vlf9h8ntf" data-path="src/pages/HomePage.tsx" />
                Free 14-day trial
              </div>
              <div className="flex items-center" data-id="vt7ii5bnu" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" data-id="wkt7pldgi" data-path="src/pages/HomePage.tsx" />
                No credit card required
              </div>
              <div className="flex items-center" data-id="85iladvhi" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" data-id="jua8ye16o" data-path="src/pages/HomePage.tsx" />
                Cancel anytime
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-4 py-16 bg-white/50" data-id="nxn3b5lqg" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="wupz6gak4" data-path="src/pages/HomePage.tsx">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="0c4ikpuo3" data-path="src/pages/HomePage.tsx">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center" data-id="ak8vjjlhy" data-path="src/pages/HomePage.tsx">
                  <div className="flex justify-center mb-3" data-id="lo05ipbqy" data-path="src/pages/HomePage.tsx">
                    <Icon className="h-8 w-8 text-blue-500" data-id="dh5waey6d" data-path="src/pages/HomePage.tsx" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="ne1p4e5s4" data-path="src/pages/HomePage.tsx">{stat.value}</div>
                  <div className="text-gray-600" data-id="2fqu1i8az" data-path="src/pages/HomePage.tsx">{stat.label}</div>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="px-4 py-20" data-id="2gvyuyvwr" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="bz7ks2kfl" data-path="src/pages/HomePage.tsx">
          <div className="text-center mb-16" data-id="kl5vqwkix" data-path="src/pages/HomePage.tsx">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="eqbd44jum" data-path="src/pages/HomePage.tsx">
              Everything You Need in One Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="kz929d163" data-path="src/pages/HomePage.tsx">
              Comprehensive AI-powered tools designed specifically for modern educators
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="ozwwaemyi" data-path="src/pages/HomePage.tsx">
            {mainFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }} data-id="sbtv3kw7x" data-path="src/pages/HomePage.tsx">
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm group hover:scale-105" data-id="yittzlgnj" data-path="src/pages/HomePage.tsx">
                    <CardHeader data-id="1yh1uolwf" data-path="src/pages/HomePage.tsx">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${feature.color} w-fit mb-4 group-hover:scale-110 transition-transform`} data-id="cklnb1ifj" data-path="src/pages/HomePage.tsx">
                        <Icon className="h-8 w-8 text-white" data-id="1n6o6qpql" data-path="src/pages/HomePage.tsx" />
                      </div>
                      <CardTitle className="text-xl" data-id="da2smnz1u" data-path="src/pages/HomePage.tsx">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent data-id="2nwg1c81e" data-path="src/pages/HomePage.tsx">
                      <CardDescription className="text-base leading-relaxed mb-4" data-id="z843dpejb" data-path="src/pages/HomePage.tsx">
                        {feature.description}
                      </CardDescription>
                      <ul className="space-y-2" data-id="dr96mvyla" data-path="src/pages/HomePage.tsx">
                        {feature.features.map((item, i) =>
                        <li key={i} className="flex items-center text-sm text-gray-600" data-id="l4izp14gy" data-path="src/pages/HomePage.tsx">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" data-id="x3g9lngpk" data-path="src/pages/HomePage.tsx" />
                            {item}
                          </li>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="5nr33i5jw" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="7u3t5itby" data-path="src/pages/HomePage.tsx">
          <div className="text-center mb-16" data-id="gzh3hoa4b" data-path="src/pages/HomePage.tsx">
            <Badge className="mb-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2" data-id="e6ed3k18t" data-path="src/pages/HomePage.tsx">
              <Target className="h-4 w-4 mr-2" data-id="e52qmi999" data-path="src/pages/HomePage.tsx" />
              Real-World Success Stories
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="jatjqvlpc" data-path="src/pages/HomePage.tsx">
              See How Educators Are Transforming Their Teaching
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="now8k0o6k" data-path="src/pages/HomePage.tsx">
              From K-12 teachers to corporate trainers, discover how AI is revolutionizing education across all sectors
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="jc0ab0lud" data-path="src/pages/HomePage.tsx">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }} data-id="zqvc89jn9" data-path="src/pages/HomePage.tsx">
                  <Card className="h-full bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300" data-id="cx3sairnc" data-path="src/pages/HomePage.tsx">
                    <CardHeader className="pb-4" data-id="gv2lxt5zg" data-path="src/pages/HomePage.tsx">
                      <div className={`p-4 rounded-xl bg-gradient-to-r ${useCase.color} w-fit mb-4`} data-id="n2m925ftm" data-path="src/pages/HomePage.tsx">
                        <Icon className="h-8 w-8 text-white" data-id="msikzsujw" data-path="src/pages/HomePage.tsx" />
                      </div>
                      <CardTitle className="text-xl text-gray-900" data-id="gkytp0bp4" data-path="src/pages/HomePage.tsx">{useCase.title}</CardTitle>
                    </CardHeader>
                    <CardContent data-id="ayjvnci1u" data-path="src/pages/HomePage.tsx">
                      <p className="text-gray-600 leading-relaxed mb-6" data-id="73802nnda" data-path="src/pages/HomePage.tsx">
                        {useCase.description}
                      </p>
                      
                      <div className="space-y-3 mb-6" data-id="tt6r1kak0" data-path="src/pages/HomePage.tsx">
                        {useCase.benefits.map((benefit, i) =>
                        <div key={i} className="flex items-center" data-id="1w3oj5kzp" data-path="src/pages/HomePage.tsx">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" data-id="ydmu4a0pr" data-path="src/pages/HomePage.tsx" />
                            <span className="text-sm text-gray-700 font-medium" data-id="6hobjdmjz" data-path="src/pages/HomePage.tsx">{benefit}</span>
                          </div>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => navigate('/register')}
                        className={`w-full bg-gradient-to-r ${useCase.color} hover:opacity-90 transition-opacity`} data-id="itbrxy3bc" data-path="src/pages/HomePage.tsx">
                        {useCase.cta}
                        <ArrowRight className="ml-2 h-4 w-4" data-id="oa01cvr5i" data-path="src/pages/HomePage.tsx" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-gray-50 to-gray-100" data-id="ohnynt5fr" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="868dbyl59" data-path="src/pages/HomePage.tsx">
          <div className="text-center mb-16" data-id="jrttiojb4" data-path="src/pages/HomePage.tsx">
            <Badge className="mb-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2" data-id="3ip0qb4sq" data-path="src/pages/HomePage.tsx">
              <Brain className="h-4 w-4 mr-2" data-id="7sjczwmh6" data-path="src/pages/HomePage.tsx" />
              Advanced AI Technology
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="jkps16ceh" data-path="src/pages/HomePage.tsx">
              Powered by Cutting-Edge AI
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="ljr1kejik" data-path="src/pages/HomePage.tsx">
              Our AI doesn't just generate content - it understands education, adapts to your style, and grows with your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="kansbuwqt" data-path="src/pages/HomePage.tsx">
            {aiCapabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={capability.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-sm" data-id="bkwj9rmqx" data-path="src/pages/HomePage.tsx">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg" data-id="xxqlag3yt" data-path="src/pages/HomePage.tsx">
                    <Icon className="h-6 w-6 text-white" data-id="ms2yqn02i" data-path="src/pages/HomePage.tsx" />
                  </div>
                  <div data-id="zmw9dqcis" data-path="src/pages/HomePage.tsx">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="e5g8bm8de" data-path="src/pages/HomePage.tsx">{capability.title}</h3>
                    <p className="text-gray-600 leading-relaxed" data-id="fjupy31yk" data-path="src/pages/HomePage.tsx">{capability.description}</p>
                  </div>
                </motion.div>);

            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="px-4 py-20" data-id="kb6wq31p0" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="rlcd1la20" data-path="src/pages/HomePage.tsx">
          <div className="text-center mb-16" data-id="xbi53cte9" data-path="src/pages/HomePage.tsx">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="oy2pv1szp" data-path="src/pages/HomePage.tsx">
              Trusted by Educators Worldwide
            </h2>
            <p className="text-xl text-gray-600" data-id="1q800kp6c" data-path="src/pages/HomePage.tsx">
              See how teachers are transforming their classrooms with AI
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="mugmfuhzm" data-path="src/pages/HomePage.tsx">
            {testimonials.map((testimonial, index) =>
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }} data-id="7bnbp9we6" data-path="src/pages/HomePage.tsx">
                <Card className="h-full bg-white/80 backdrop-blur-sm border-0 shadow-lg" data-id="gue9d22zl" data-path="src/pages/HomePage.tsx">
                  <CardContent className="p-6" data-id="7s2jh7qa2" data-path="src/pages/HomePage.tsx">
                    <div className="flex items-center mb-4" data-id="xdrqsqiru" data-path="src/pages/HomePage.tsx">
                      {[...Array(testimonial.rating)].map((_, i) =>
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" data-id="01ub29lcx" data-path="src/pages/HomePage.tsx" />
                    )}
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="efi3oir3x" data-path="src/pages/HomePage.tsx">
                      "{testimonial.content}"
                    </p>
                    <div data-id="fnv3tnfkm" data-path="src/pages/HomePage.tsx">
                      <p className="font-semibold text-gray-900" data-id="dkwmxikr3" data-path="src/pages/HomePage.tsx">{testimonial.name}</p>
                      <p className="text-sm text-gray-500" data-id="98q2evrxs" data-path="src/pages/HomePage.tsx">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-20 bg-gray-50" data-id="y1a2shppx" data-path="src/pages/HomePage.tsx">
        <div className="max-w-4xl mx-auto" data-id="szvtrtxjj" data-path="src/pages/HomePage.tsx">
          <div className="text-center mb-16" data-id="utwjrbimm" data-path="src/pages/HomePage.tsx">
            <Badge className="mb-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2" data-id="cc81l3mi9" data-path="src/pages/HomePage.tsx">
              <Headphones className="h-4 w-4 mr-2" data-id="20p45zr1k" data-path="src/pages/HomePage.tsx" />
              Frequently Asked Questions
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="ih4qeubz0" data-path="src/pages/HomePage.tsx">
              Got Questions? We've Got Answers
            </h2>
            <p className="text-xl text-gray-600" data-id="tipe9bzo1" data-path="src/pages/HomePage.tsx">
              Everything you need to know about Soyous AI and how it can transform your teaching
            </p>
          </div>
          
          <div className="space-y-4" data-id="b4n6nm2ux" data-path="src/pages/HomePage.tsx">
            {faqItems.map((item, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }} data-id="1ezc8jzds" data-path="src/pages/HomePage.tsx">
                <Card className="bg-white shadow-sm border-0" data-id="amrobcsh1" data-path="src/pages/HomePage.tsx">
                  <CardHeader data-id="aipjs2ada" data-path="src/pages/HomePage.tsx">
                    <button
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    className="w-full flex items-center justify-between text-left" data-id="y0309ka2x" data-path="src/pages/HomePage.tsx">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="p4m5pjv31" data-path="src/pages/HomePage.tsx">{item.question}</h3>
                      {expandedFAQ === index ?
                    <ChevronUp className="h-5 w-5 text-gray-500" data-id="ptpvzkn3m" data-path="src/pages/HomePage.tsx" /> :

                    <ChevronDown className="h-5 w-5 text-gray-500" data-id="xg76zth28" data-path="src/pages/HomePage.tsx" />
                    }
                    </button>
                  </CardHeader>
                  {expandedFAQ === index &&
                <CardContent className="pt-0" data-id="2cg4wcw09" data-path="src/pages/HomePage.tsx">
                      <p className="text-gray-600 leading-relaxed" data-id="9yqnbsb1n" data-path="src/pages/HomePage.tsx">{item.answer}</p>
                    </CardContent>
                }
                </Card>
              </motion.div>
            )}
          </div>
          
          <div className="text-center mt-12" data-id="71mft1o5y" data-path="src/pages/HomePage.tsx">
            <p className="text-gray-600 mb-4" data-id="spv4dg6pc" data-path="src/pages/HomePage.tsx">Still have questions?</p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700" data-id="00slykc05" data-path="src/pages/HomePage.tsx">
              Get Started Free
              <ArrowRight className="ml-2 h-4 w-4" data-id="6mxftr8nd" data-path="src/pages/HomePage.tsx" />
            </Button>
          </div>
        </div>
      </section>

      {/* Android Download Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-green-50 to-blue-50" data-id="fe6npwv4t" data-path="src/pages/HomePage.tsx">
        <div className="max-w-4xl mx-auto text-center" data-id="of6b3pars" data-path="src/pages/HomePage.tsx">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} data-id="eqb8pc6la" data-path="src/pages/HomePage.tsx">
            
            <Badge className="mb-6 bg-gradient-to-r from-green-500 to-blue-600 text-white px-4 py-2" data-id="d1w1qp6kl" data-path="src/pages/HomePage.tsx">
              <Smartphone className="h-4 w-4 mr-2" data-id="7jm6xl2o7" data-path="src/pages/HomePage.tsx" />
              Mobile App Available
            </Badge>
            
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="074i3grd9" data-path="src/pages/HomePage.tsx">
              Download for Android
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" data-id="r052spfxr" data-path="src/pages/HomePage.tsx">
              Take your AI teaching assistant with you anywhere. Download the Soyous AI mobile app for seamless teaching on the go.
            </p>
            
            <div className="flex flex-col md:flex-row items-center justify-center space-y-8 md:space-y-0 md:space-x-12" data-id="63r0zyewj" data-path="src/pages/HomePage.tsx">
              {/* QR Code */}
              <div className="flex flex-col items-center" data-id="ftna6v2n3" data-path="src/pages/HomePage.tsx">
                <div className="bg-white p-6 rounded-lg shadow-lg border-2 border-gray-200" data-id="wi3o49vno" data-path="src/pages/HomePage.tsx">
                  <img
                    src="data:image/png;base64,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"
                    alt="QR Code to download the app"
                    className="w-48 h-48 object-contain" data-id="08x2jy5d6" data-path="src/pages/HomePage.tsx" />

                </div>
                <p className="text-gray-600 mt-4 text-center" data-id="fo68kpyio" data-path="src/pages/HomePage.tsx">
                  Scan the QR code to download the app
                </p>
              </div>
              
              {/* Download Button */}
              <div className="flex flex-col items-center space-y-4" data-id="robd9y2px" data-path="src/pages/HomePage.tsx">
                <div className="text-center" data-id="yeg8jx5si" data-path="src/pages/HomePage.tsx">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2" data-id="jrbmwq0ud" data-path="src/pages/HomePage.tsx">
                    Direct Download
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="30gef1phu" data-path="src/pages/HomePage.tsx">
                    Or click the button below to download directly
                  </p>
                </div>
                
                <a
                  href="https://newoaks.s3.us-west-1.amazonaws.com/AutoDev/20900/3868e6d5-5c70-43ae-832c-0f10d5762cf3.apk"
                  download="soyous-ai-teacher-assistant.apk"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105" data-id="56kv9yj8o" data-path="src/pages/HomePage.tsx">

                  <Download className="h-5 w-5 mr-2" data-id="b06ymkp9f" data-path="src/pages/HomePage.tsx" />
                  Download for Android
                  <ExternalLink className="h-4 w-4 ml-2" data-id="8ykbyvdyl" data-path="src/pages/HomePage.tsx" />
                </a>
                
                <div className="text-sm text-gray-500 text-center mt-4" data-id="05egdtd4u" data-path="src/pages/HomePage.tsx">
                  <p data-id="zc0eaeuqy" data-path="src/pages/HomePage.tsx">Compatible with Android 5.0 and above</p>
                  <p data-id="tthyllpdm" data-path="src/pages/HomePage.tsx">File size: ~50MB</p>
                </div>
              </div>
            </div>
            
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="lj7pw8iq3" data-path="src/pages/HomePage.tsx">
              <div className="flex items-center justify-center space-x-3 p-4 bg-white rounded-lg shadow-sm" data-id="huc1cx3r8" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" data-id="g6leiekme" data-path="src/pages/HomePage.tsx" />
                <span className="text-gray-700" data-id="lnh5hy49a" data-path="src/pages/HomePage.tsx">Offline lesson planning</span>
              </div>
              <div className="flex items-center justify-center space-x-3 p-4 bg-white rounded-lg shadow-sm" data-id="20p5n7cs3" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" data-id="ic434ga17" data-path="src/pages/HomePage.tsx" />
                <span className="text-gray-700" data-id="i2bythe7t" data-path="src/pages/HomePage.tsx">Sync across devices</span>
              </div>
              <div className="flex items-center justify-center space-x-3 p-4 bg-white rounded-lg shadow-sm" data-id="ptnmui2wx" data-path="src/pages/HomePage.tsx">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" data-id="fv4vwffi2" data-path="src/pages/HomePage.tsx" />
                <span className="text-gray-700" data-id="z5euxsfy1" data-path="src/pages/HomePage.tsx">Push notifications</span>
              </div>
            </div>
            
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-gray-50 to-gray-100" data-id="28pwp1gvi" data-path="src/pages/HomePage.tsx">
        <div className="max-w-4xl mx-auto text-center" data-id="f8sb3hyf9" data-path="src/pages/HomePage.tsx">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="6uu09o8n4" data-path="src/pages/HomePage.tsx">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 mb-12" data-id="ojxd1svk5" data-path="src/pages/HomePage.tsx">
            Start with a free trial, then choose the plan that works for you
          </p>
          
          <Card className="bg-white border-2 border-blue-500 shadow-2xl" data-id="ih1hxl0ry" data-path="src/pages/HomePage.tsx">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg" data-id="104efn0p4" data-path="src/pages/HomePage.tsx">
              <div className="flex items-center justify-center space-x-2" data-id="05zw9qbtx" data-path="src/pages/HomePage.tsx">
                <Award className="h-6 w-6" data-id="5uxvkq4xz" data-path="src/pages/HomePage.tsx" />
                <CardTitle className="text-2xl" data-id="59k93lh36" data-path="src/pages/HomePage.tsx">Complete AI Teaching Platform</CardTitle>
              </div>
              <CardDescription className="text-blue-100" data-id="2qhxlokt4" data-path="src/pages/HomePage.tsx">
                Everything you need to transform your teaching
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8" data-id="yjuqbuidg" data-path="src/pages/HomePage.tsx">
              <div className="text-center mb-8" data-id="04jct95vv" data-path="src/pages/HomePage.tsx">
                <div className="text-5xl font-bold text-gray-900 mb-2" data-id="46wvkqdez" data-path="src/pages/HomePage.tsx">Free Trial</div>
                <div className="text-gray-600" data-id="eq40rrhx2" data-path="src/pages/HomePage.tsx">14 days • No credit card required</div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="nl0ke81r3" data-path="src/pages/HomePage.tsx">
                {pricingFeatures.map((feature, index) =>
                <div key={index} className="flex items-center" data-id="9bmvpv4i0" data-path="src/pages/HomePage.tsx">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" data-id="e1f6aksl1" data-path="src/pages/HomePage.tsx" />
                    <span className="text-gray-700" data-id="r7abfd1u2" data-path="src/pages/HomePage.tsx">{feature}</span>
                  </div>
                )}
              </div>
              
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 py-4 text-lg" data-id="zq1wbyivg" data-path="src/pages/HomePage.tsx">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" data-id="x5v9h6vf1" data-path="src/pages/HomePage.tsx" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-20 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" data-id="j53j742ly" data-path="src/pages/HomePage.tsx">
        <div className="max-w-4xl mx-auto text-center text-white" data-id="4ns6az6nv" data-path="src/pages/HomePage.tsx">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} data-id="dpe0ra8fb" data-path="src/pages/HomePage.tsx">
            
            <Rocket className="h-16 w-16 mx-auto mb-6 text-white" data-id="e0fnrwhuo" data-path="src/pages/HomePage.tsx" />
            <h2 className="text-5xl font-bold mb-6" data-id="tprxa9kvo" data-path="src/pages/HomePage.tsx">
              Ready to Transform Education?
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto" data-id="wu7o570fh" data-path="src/pages/HomePage.tsx">
              Join thousands of educators who are already using AI to create more engaging, 
              effective, and personalized learning experiences for their students.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8" data-id="jhdld0a6j" data-path="src/pages/HomePage.tsx">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold" data-id="m4k95ncbp" data-path="src/pages/HomePage.tsx">
                Get Started Free Today
                <ArrowRight className="ml-2 h-5 w-5" data-id="a7kvz6267" data-path="src/pages/HomePage.tsx" />
              </Button>

            </div>
            
            <div className="flex items-center justify-center space-x-6 text-blue-100" data-id="63hofo2pe" data-path="src/pages/HomePage.tsx">
              <div className="flex items-center" data-id="z5txf6m1b" data-path="src/pages/HomePage.tsx">
                <Clock className="h-5 w-5 mr-2" data-id="y3zalnvk8" data-path="src/pages/HomePage.tsx" />
                Setup in under 2 minutes
              </div>
              <div className="flex items-center" data-id="q7qb7ujuo" data-path="src/pages/HomePage.tsx">
                <Globe className="h-5 w-5 mr-2" data-id="5887mgxar" data-path="src/pages/HomePage.tsx" />
                Works on all devices
              </div>
              <div className="flex items-center" data-id="3snn250kv" data-path="src/pages/HomePage.tsx">
                <Users className="h-5 w-5 mr-2" data-id="54wtam59r" data-path="src/pages/HomePage.tsx" />
                2,500+ educators trust us
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white px-4 py-16" data-id="o8j44nvbb" data-path="src/pages/HomePage.tsx">
        <div className="max-w-7xl mx-auto" data-id="jvuwzuavc" data-path="src/pages/HomePage.tsx">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8" data-id="k1501nvn9" data-path="src/pages/HomePage.tsx">
            <div className="col-span-1 md:col-span-2" data-id="i2s867ew9" data-path="src/pages/HomePage.tsx">
              <div className="flex items-center space-x-3 mb-4" data-id="fma0kome6" data-path="src/pages/HomePage.tsx">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg" data-id="etoc70m1q" data-path="src/pages/HomePage.tsx">
                  <GraduationCap className="h-6 w-6 text-white" data-id="8pwzayaq9" data-path="src/pages/HomePage.tsx" />
                </div>
                <span className="text-xl font-bold" data-id="fqossf4aj" data-path="src/pages/HomePage.tsx">Soyous AI</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md" data-id="2axuoqz1x" data-path="src/pages/HomePage.tsx">
                Empowering educators worldwide with cutting-edge AI technology to create 
                more engaging and effective learning experiences.
              </p>
              <div className="flex space-x-4" data-id="inr8x7bh0" data-path="src/pages/HomePage.tsx">
                <div className="flex items-center space-x-2 text-sm text-gray-400" data-id="pz0bri4v5" data-path="src/pages/HomePage.tsx">
                  <Monitor className="h-4 w-4" data-id="3m1c3wh7s" data-path="src/pages/HomePage.tsx" />
                  <span data-id="nr6ium5om" data-path="src/pages/HomePage.tsx">Desktop</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400" data-id="m7kvlqams" data-path="src/pages/HomePage.tsx">
                  <Tablet className="h-4 w-4" data-id="e4f51b5hj" data-path="src/pages/HomePage.tsx" />
                  <span data-id="8273xuxht" data-path="src/pages/HomePage.tsx">Tablet</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-400" data-id="zbt6kkjz5" data-path="src/pages/HomePage.tsx">
                  <Smartphone className="h-4 w-4" data-id="ytzznd87q" data-path="src/pages/HomePage.tsx" />
                  <span data-id="npy3rmg6u" data-path="src/pages/HomePage.tsx">Mobile</span>
                </div>
              </div>
            </div>
            
            <div data-id="ezp5c1iyj" data-path="src/pages/HomePage.tsx">
              <h3 className="text-lg font-semibold mb-4" data-id="061gkybbs" data-path="src/pages/HomePage.tsx">Features</h3>
              <ul className="space-y-2 text-gray-400" data-id="i3hja404d" data-path="src/pages/HomePage.tsx">
                <li data-id="phjmfy17y" data-path="src/pages/HomePage.tsx">AI Lesson Plans</li>
                <li data-id="vyuksp04y" data-path="src/pages/HomePage.tsx">Presentation Generator</li>
                <li data-id="ly7nfmtvg" data-path="src/pages/HomePage.tsx">Quiz Builder</li>
                <li data-id="pskoohluy" data-path="src/pages/HomePage.tsx">Gamification</li>
                <li data-id="ugs1s7qxb" data-path="src/pages/HomePage.tsx">Grade Management</li>
                <li data-id="63p2l8vg0" data-path="src/pages/HomePage.tsx">Plagiarism Detection</li>
              </ul>
            </div>
            
            <div data-id="y4sd5lccf" data-path="src/pages/HomePage.tsx">
              <h3 className="text-lg font-semibold mb-4" data-id="o9g8ftvz2" data-path="src/pages/HomePage.tsx">Support</h3>
              <ul className="space-y-2 text-gray-400" data-id="kcw7us4zx" data-path="src/pages/HomePage.tsx">
                <li data-id="1qpk3oxdz" data-path="src/pages/HomePage.tsx">Documentation</li>
                <li data-id="jgewmsho8" data-path="src/pages/HomePage.tsx">Community Forum</li>
                <li data-id="ik1wil8fm" data-path="src/pages/HomePage.tsx">Contact Support</li>
                <li data-id="jbbe2bkk0" data-path="src/pages/HomePage.tsx">Feature Requests</li>
                <li data-id="m9exrmf34" data-path="src/pages/HomePage.tsx">Status Page</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center" data-id="rq3sq1iqr" data-path="src/pages/HomePage.tsx">
            <p className="text-gray-400 mb-2" data-id="8ocngbh3e" data-path="src/pages/HomePage.tsx">
              © 2025 Soyous AI Teacher Assistant. All rights reserved.
            </p>
            <p className="text-sm text-gray-500" data-id="okupa5fev" data-path="src/pages/HomePage.tsx">
              Powered by SE Business Solutions • Built with ❤️ for educators
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default HomePage;